import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Share2, Copy, CheckCircle, Twitter, Facebook, Linkedin, Mail, Link2 } from 'lucide-react';

const ShareResultsCard = ({ userId, pairingCode, userName }) => {
  const [shareUrl, setShareUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const generateShareLink = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/share/generate-link`);
      setShareUrl(response.data.share_url);
    } catch (error) {
      toast.error('Failed to generate share link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const shareText = `I just discovered my BUILDR profile! I'm a ${pairingCode} - find out your unique strengths too!`;
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(shareUrl || '');

  const socialLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=My%20BUILDR%20Profile&summary=${encodedText}`,
    email: `mailto:?subject=Check out my BUILDR Profile!&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Share2 className="h-5 w-5 text-indigo-600" />
          Share Your Results
        </CardTitle>
        <CardDescription>
          Invite others to discover their BUILDR profile
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!shareUrl ? (
          <div className="text-center py-4">
            <p className="text-gray-600 mb-4">
              Generate a shareable link to your BUILDR profile. When others view it, they'll be invited to take the assessment too!
            </p>
            <Button 
              onClick={generateShareLink} 
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {loading ? 'Generating...' : 'Generate Share Link'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Share URL */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Your Share Link</label>
              <div className="flex gap-2">
                <Input 
                  value={shareUrl} 
                  readOnly 
                  className="bg-gray-50"
                />
                <Button 
                  onClick={copyToClipboard}
                  variant="outline"
                  className={copied ? 'bg-green-50 border-green-300' : ''}
                >
                  {copied ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Social Share Buttons */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Share on Social Media</label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 hover:bg-blue-50 hover:border-blue-300"
                  onClick={() => window.open(socialLinks.twitter, '_blank')}
                >
                  <Twitter className="h-4 w-4 mr-2 text-blue-400" />
                  Twitter
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 hover:bg-blue-50 hover:border-blue-300"
                  onClick={() => window.open(socialLinks.facebook, '_blank')}
                >
                  <Facebook className="h-4 w-4 mr-2 text-blue-600" />
                  Facebook
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 hover:bg-blue-50 hover:border-blue-300"
                  onClick={() => window.open(socialLinks.linkedin, '_blank')}
                >
                  <Linkedin className="h-4 w-4 mr-2 text-blue-700" />
                  LinkedIn
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full mt-2 hover:bg-gray-50"
                onClick={() => window.open(socialLinks.email, '_blank')}
              >
                <Mail className="h-4 w-4 mr-2" />
                Share via Email
              </Button>
            </div>

            {/* Preview what they'll see */}
            <div className="bg-indigo-50 p-4 rounded-lg">
              <p className="text-sm text-indigo-800 font-medium mb-2">What others will see:</p>
              <p className="text-sm text-indigo-700">
                "{userName}'s BUILDR Profile - {pairingCode}" with an invitation to discover their own strengths.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ShareResultsCard;
